import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { useActivity } from "@/context/ActivityContext";
import { useAuth } from "@/context/AuthContext";
import BackButton from "@/components/BackButton";
import StateControl from "@/components/StateControl";
import EnrollmentSection from "@/components/EnrollmentSection";
import StartingGrid from "@/components/StartingGrid";
import { StateBadge } from "@/lib/states";
import { Medal, ArrowLeft, Timer, Flag, Share2, Radio } from "lucide-react";

const SESSION_LABEL = { gara: "Gara", prove_libere: "Prove Libere", qualifica: "Qualifica" };

const medalColor = (pos) =>
  pos === 1 ? "text-yellow-400" : pos === 2 ? "text-zinc-300" : pos === 3 ? "text-amber-600" : "text-zinc-500";

// Converte un tempo giro (es. "1:23.456", "23.4", "1.23.456") in secondi. null se non valido.
function timeToSeconds(raw) {
  if (!raw) return null;
  const s = String(raw).trim().replace(",", ".");
  if (!s) return null;
  const m = s.match(/^(?:(\d+):)?(\d{1,2})(?:\.(\d{1,3}))?$/);
  if (m) {
    const min = parseInt(m[1] || "0", 10);
    const sec = parseInt(m[2] || "0", 10);
    const ms = m[3] ? parseInt(m[3].padEnd(3, "0"), 10) : 0;
    return min * 60 + sec + ms / 1000;
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function ResultsTable({ rows, recordId, session }) {
  if (!rows.length) {
    return (
      <p className="text-zinc-500 py-8 text-center text-sm" data-testid={`empty-results-${session}`}>
        Risultati non ancora pubblicati.
      </p>
    );
  }
  return (
    <div className="border border-zinc-800" data-testid={`results-table-${session}`}>
      <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-zinc-900 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
        <div className="col-span-2">Pos</div>
        <div className="col-span-4">Pilota</div>
        <div className="col-span-2 text-center">Tempo</div>
        <div className="col-span-2 text-center">Penalità</div>
        <div className="col-span-2 text-right">Punti</div>
      </div>
      {rows.map((r, i) => {
        const fp = r.final_position ?? r.position;
        const penalized = (r.penalty_positions || 0) !== 0 || (r.penalty_seconds || 0) !== 0;
        const isRecord = recordId && r.id === recordId;
        return (
          <motion.div key={r.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.04 }}
            data-testid={isRecord ? "result-row-record" : undefined}
            className={`grid grid-cols-12 gap-2 px-5 py-4 items-center border-t border-zinc-800 ${isRecord ? "bg-fuchsia-500/10" : i % 2 ? "bg-transparent" : "bg-zinc-900/50"}`}>
            <div className={`col-span-2 font-display font-bold text-lg ${medalColor(fp)}`}>
              {fp <= 3 ? <Medal className={`h-5 w-5 ${medalColor(fp)}`} /> : fp}
            </div>
            <div className="col-span-4 font-heading font-bold text-zinc-50">
              {r.name}
              {r.penalty_positions ? (
                <span className="ml-2 text-xs text-zinc-500 font-body">(part. P{r.position})</span>
              ) : null}
            </div>
            <div className={`col-span-2 text-center font-display text-sm tabular-nums ${isRecord ? "text-fuchsia-300 font-bold" : "text-cyan-400"}`}>
              {r.time || <span className="text-zinc-600">—</span>}
            </div>
            <div className="col-span-2 text-center text-xs font-bold">
              {penalized ? (
                <span className="text-red-400">
                  {r.penalty_seconds ? `+${r.penalty_seconds}s ` : ""}
                  {r.penalty_positions ? `+${r.penalty_positions}pos` : ""}
                </span>
              ) : (
                <span className="text-zinc-600">—</span>
              )}
            </div>
            <div className="col-span-2 text-right font-display font-bold text-xl text-yellow-400">{r.points}</div>
          </motion.div>
        );
      })}
    </div>
  );
}

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [results, setResults] = useState([]);
  const [trackRecord, setTrackRecord] = useState(null);
  const [error, setError] = useState(false);
  const [champ, setChamp] = useState(null);
  const { tick } = useActivity();
  const { isAdmin, user } = useAuth();

  useEffect(() => {
    setError(false);
    api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => setError(true));
    api.get(`/events/${id}/results`).then((r) => setResults(r.data)).catch(() => {});
    api.get(`/events/${id}/track-record`).then((r) => setTrackRecord(r.data)).catch(() => {});
  }, [id, tick]);

  useEffect(() => {
    if (event?.championship_id) api.get(`/championships/${event.championship_id}`).then((r) => setChamp(r.data)).catch(() => {});
  }, [event?.championship_id]);

  const reloadEvent = () => api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => {});
  const canManage = isAdmin || !!(user && user.id && champ && champ.owner_id && champ.owner_id === user.id);

  const sharePodium = async (rows) => {
    const top = [...rows].sort((a, b) => (a.final_position ?? a.position) - (b.final_position ?? b.position)).slice(0, 3);
    if (!top.length) return;
    const accent = champ?.category === "rc" ? "#facc15" : "#06b6d4";
    const W = 1080, H = 1080;
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#09090b"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = accent; ctx.fillRect(0, 0, W, 10);
    const logo = new Image();
    logo.src = "/team-tuono-logo.png";
    await new Promise((res) => { logo.onload = res; logo.onerror = res; });
    if (logo.complete && logo.naturalWidth) {
      try { ctx.drawImage(logo, W - 168, 44, 120, 120); } catch (e) { /* skip */ }
    }
    ctx.textAlign = "left";
    ctx.fillStyle = accent; ctx.font = "bold 28px Arial"; ctx.fillText("TEAM TUONO", 56, 92);
    ctx.fillStyle = "#fafafa"; ctx.font = "900 62px Arial"; ctx.fillText((event.name || "").toUpperCase().slice(0, 20), 56, 172);
    ctx.fillStyle = "#a1a1aa"; ctx.font = "26px Arial";
    const sub = [event.track || event.location, event.date].filter(Boolean).join(" · ");
    ctx.fillText(("PODIO" + (sub ? " · " + sub : "")).slice(0, 46), 56, 224);

    const base = 950;
    const bars = [
      { pos: 2, cx: 272, w: 236, h: 300 },
      { pos: 1, cx: 540, w: 260, h: 410 },
      { pos: 3, cx: 808, w: 236, h: 236 },
    ];
    const color = (p) => (p === 1 ? "#facc15" : p === 2 ? "#d4d4d8" : "#d97706");
    const byPos = {};
    top.forEach((r) => { byPos[r.final_position ?? r.position] = r; });
    bars.forEach((b) => {
      const r = byPos[b.pos];
      if (!r) return;
      const x = b.cx - b.w / 2, y = base - b.h;
      ctx.fillStyle = "#141417"; ctx.fillRect(x, y, b.w, b.h);
      ctx.fillStyle = color(b.pos); ctx.fillRect(x, y, b.w, 8);
      ctx.textAlign = "center";
      ctx.fillStyle = color(b.pos); ctx.font = "900 96px Arial";
      ctx.fillText(String(b.pos), b.cx, base - b.h / 2 + 24);
      ctx.fillStyle = "#fafafa"; ctx.font = "bold 32px Arial";
      ctx.fillText((r.name || "").slice(0, 16), b.cx, y - 48);
      ctx.fillStyle = accent; ctx.font = "bold 26px Arial";
      ctx.fillText(r.points ? r.points + " pt" : (r.time || ""), b.cx, y - 14);
    });
    ctx.textAlign = "left";

    const blob = await new Promise((res) => c.toBlob(res, "image/png"));
    const file = new File([blob], "podio.png", { type: "image/png" });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: event.name, text: `Podio ${event.name} — Team Tuono` });
        return;
      }
    } catch (e) { /* fallback to download */ }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `podio-${event.name}.png`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Locandina podio generata");
  };

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
        <BackButton className="mb-6" />
        <p className="text-zinc-400" data-testid="event-error">
          Impossibile caricare il Gran Premio. Potrebbe non essere più disponibile.
        </p>
      </div>
    );
  }

  if (!event) return <div className="max-w-7xl mx-auto px-6 py-20 text-zinc-500">Caricamento…</div>;

  // Record della pista dell'evento: miglior tempo (più basso) tra TUTTE le sessioni
  let record = null;
  for (const r of results) {
    const secs = timeToSeconds(r.time);
    if (secs != null && (record == null || secs < record.secs)) {
      record = { secs, name: r.name, time: r.time, id: r.id };
    }
  }

  const sessions = event.session_types && event.session_types.length ? event.session_types : [event.session_type || "gara"];
  const bySession = {};
  for (const r of results) {
    const s = r.session || "gara";
    (bySession[s] = bySession[s] || []).push(r);
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
      <BackButton className="mb-6" />
      <Link to={`/campionati/${event.championship_id}`} data-testid="back-to-champ"
        className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 hover:text-yellow-400 transition-colors duration-200">
        <ArrowLeft className="h-4 w-4" /> Campionato
      </Link>
      <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-black tracking-tighter uppercase text-zinc-50">
        {event.name}
      </h1>
      <p className="mt-2 text-zinc-400">{[event.date, event.time, event.track || event.location].filter(Boolean).join(" · ")}</p>
      {(event.car_model || event.cost > 0 || (event.duration_type && event.duration_value)) && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="event-meta">
          {event.duration_type && event.duration_value && (
            <span className="inline-flex items-center gap-1.5 border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 text-xs font-bold px-3 py-1.5 rounded-full" data-testid="event-duration-badge">
              ⏱ {event.duration_type === "giri" ? `${event.duration_value} giri` : `${event.duration_value} min`}
            </span>
          )}
          {event.car_model && (
            <span className="inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-300 text-xs font-bold px-3 py-1.5 rounded-full" data-testid="event-car-model-badge">
              🏎️ {event.car_model}
            </span>
          )}
          {event.cost > 0 && (
            <span className="inline-flex items-center gap-1.5 border border-yellow-400/40 bg-yellow-400/10 text-yellow-400 text-xs font-bold px-3 py-1.5 rounded-full" data-testid="event-cost-badge">
              Quota €{event.cost}
            </span>
          )}
        </div>
      )}
      {event.state && <div className="mt-3"><StateBadge state={event.state} testid="event-state-badge" /></div>}

      {(event.stream_url || event.server_name || event.server_password || event.game_name) && (
        <div className="mt-6 border border-zinc-800 bg-zinc-900/40 rounded-lg p-5 space-y-3" data-testid="event-access">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">Diretta & Partecipazione</div>
          {event.stream_url && (
            <a href={event.stream_url} target="_blank" rel="noreferrer" data-testid="event-stream-link"
              className="inline-flex items-center gap-2 text-sm font-bold text-red-400 hover:text-red-300 transition-colors duration-200">
              <Radio className="h-4 w-4" /> Guarda la diretta
            </a>
          )}
          {event.game_name && <p className="text-sm text-zinc-300" data-testid="event-game"><span className="text-zinc-500">Gioco:</span> <span className="font-bold">{event.game_name}</span></p>}
          {event.server_name && <p className="text-sm text-zinc-300" data-testid="event-server"><span className="text-zinc-500">Server:</span> <span className="font-bold text-cyan-400">{event.server_name}</span></p>}
          {event.server_password && <p className="text-sm text-zinc-300" data-testid="event-server-password"><span className="text-zinc-500">Password:</span> <span className="font-bold text-cyan-400">{event.server_password}</span></p>}
        </div>
      )}

      {canManage && (
        <div className="mt-6">
          <StateControl kind="events" id={id} state={event.state} enrollOverride={event.enroll_override} isAdmin={isAdmin} onChanged={reloadEvent} />
        </div>
      )}

      <div className="mt-8 border border-zinc-800 bg-zinc-900/40 p-5 rounded-lg">
        <EnrollmentSection kind="events" id={id} state={event.state} enrollOverride={event.enroll_override} />
      </div>

      <StartingGrid id={id} />

      {trackRecord?.record && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="track-record-banner"
          className="mt-6 flex items-center gap-4 rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/10 px-5 py-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-fuchsia-500/20 border border-fuchsia-400/50">
            <Timer className="h-5 w-5 text-fuchsia-300" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-fuchsia-300/80">
              Record della pista{trackRecord.location ? ` · ${trackRecord.location}` : ""}
              {trackRecord.record.is_current_event && (
                <span data-testid="track-record-new" className="rounded-full bg-fuchsia-500 text-zinc-950 px-2 py-0.5 text-[9px] tracking-wider">
                  Nuovo record
                </span>
              )}
            </div>
            <div className="mt-0.5 flex flex-wrap items-baseline gap-x-3">
              <span data-testid="track-record-driver" className="font-heading font-black text-lg text-fuchsia-100 truncate">
                {trackRecord.record.name}
              </span>
              <span data-testid="track-record-time" className="font-display font-bold text-fuchsia-300 tabular-nums">
                {trackRecord.record.time}
              </span>
              {trackRecord.record.event_name && !trackRecord.record.is_current_event && (
                <span className="text-xs text-fuchsia-300/70 font-body">· {trackRecord.record.event_name}</span>
              )}
            </div>
          </div>
        </motion.div>
      )}

      <div className="mt-10 space-y-10">
        {sessions.map((s) => (
          <div key={s} data-testid={`session-block-${s}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-cyan-400">
                <Flag className="h-4 w-4" /> {SESSION_LABEL[s] || s}
              </h2>
              {s === "gara" && (bySession[s] || []).length > 0 && (
                <button onClick={() => sharePodium(bySession[s])} data-testid="share-podium-btn"
                  className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-300 border border-zinc-700 px-3 py-1.5 rounded-full hover:border-yellow-400 hover:text-yellow-400 transition-colors duration-200">
                  <Share2 className="h-3.5 w-3.5" /> Condividi podio
                </button>
              )}
            </div>
            <ResultsTable rows={bySession[s] || []} recordId={record?.id} session={s} />
          </div>
        ))}
      </div>
    </div>
  );
}
